/**
 * newyear.js
 * ---------------------------------------------------------------------------
 * Siebte Schwesterdatei der Reihe: identische Logik, eigener Namensraum (ny-).
 *
 * Erzeugt die Luftschlangen und den Glücksklee im Hintergrund und lässt sie
 * umherziehen. Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius
 * Pixel, stieben sie auseinander und beruhigen sich danach wieder auf ihr
 * Reisetempo. Das Feuerwerk gehört nicht dazu, das läuft rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Dachlandschaft.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initSilvester } from "./silvester.js";
 *   initSilvester();
 *
 * Aktiviert wird jedes Element mit der Klasse .ny-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-newyear.
 */
export function silvester() {
    "use strict";

    var CONFIG = {
        streamers:   6,      // Anzahl Luftschlangen (Desktop, mobil halbiert)
        clovers:     5,      // Anzahl Glücksklee
        fleeRadius:  170,    // ab dieser Distanz zum Zeiger weichen sie aus (px)
        fleeForce:   1.1,    // Heftigkeit des Ausweichens
        maxSpeed:    3.6,    // Fluchttempo (px pro Frame bei 60 fps)
        streamerSpeed: 0.6,  // Reisetempo Luftschlange
        cloverSpeed:   0.3,  // Reisetempo Glücksklee
        wander:      0.06,   // Stärke der zufälligen Richtungswechsel
        calmDown:    0.012,  // wie schnell der Schreck abklingt
        margin:      70,     // ab diesem Randabstand drehen sie ein
        streamerSize: [18, 34],
        cloverSize:   [12, 22]
    };

    var RIBBON_COLORS = ["#ffd166", "#ff7a6b", "#6fd3d9", "#c79aff", "#9ce8b0", "#fff0c4"];
    var CLOVER_COLORS = ["#5cb85c", "#7ac74f", "#3fa66a"];

    var STREAMER =
        '<span class="ny-critter__body">' +
        '<i class="ny-band ny-band--l"></i>' +
        '<i class="ny-band ny-band--r"></i>' +
        '</span>';

    var CLOVER =
        '<span class="ny-critter__body"><i></i><i></i><i></i><i></i></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".ny-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isStreamer = kind === "streamer";
            var range = isStreamer ? CONFIG.streamerSize : CONFIG.cloverSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "ny-critter ny-critter--" + kind;
                el.innerHTML = isStreamer ? STREAMER : CLOVER;
                el.style.setProperty("--ny-size", size.toFixed(1) + "px");
                el.style.setProperty(
                    isStreamer ? "--ny-ribbon" : "--ny-clover",
                    pick(isStreamer ? RIBBON_COLORS : CLOVER_COLORS)
                );
                el.style.opacity = isStreamer ? rand(0.8, 1).toFixed(2) : rand(0.7, 1).toFixed(2);
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isStreamer ? CONFIG.streamerSpeed : CONFIG.cloverSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isStreamer ? size / 2 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("streamer", small ? Math.ceil(CONFIG.streamers / 2) : CONFIG.streamers);
        spawn("clover", small ? Math.ceil(CONFIG.clovers / 2) : CONFIG.clovers);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "streamer") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--ny-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.nyReady) return;
        scene.dataset.nyReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--ny-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--ny-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".ny-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-silvester");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}